import { Box, Radio, TextField, Typography } from "@mui/material";
import { useTheme, type SxProps, type Theme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CenterModal } from "../CenterModal";
import { useToast } from "../ToastProvider";
import { trpc } from "~/utils/trpc";
import { supabase } from "~/utils/supabase.client";

const REASONS = [
  "NotUsed",
  "MissingFeature",
  "BetterAlternative",
  "Expensive",
  "Bugs",
  "Privacy",
  "Other",
] as const;
type Reason = (typeof REASONS)[number];

interface DeleteAccountFlowProps {
  open: boolean;
  email: string;
  onClose: () => void;
}

const BUTTON_BASE = {
  flex: 1,
  minHeight: 52,
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: 16,
  cursor: "pointer",
} as const;

export function DeleteAccountFlow({ open, email, onClose }: DeleteAccountFlowProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const c = theme.palette.custom;
  const { showActionToast } = useToast();
  const deleteAccount = trpc.user.deleteAccount.useMutation();
  const canDeleteQuery = trpc.user.canDeleteAccount.useQuery(undefined, {
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const isBlocked = canDeleteQuery.data?.canDelete === false;
  const blockingTeams = canDeleteQuery.data?.blockingTeams ?? [];

  const [step, setStep] = useState<"reason" | "confirm">("reason");
  const [reason, setReason] = useState<Reason | null>(null);
  const [other, setOther] = useState("");
  const [confirmInput, setConfirmInput] = useState("");
  const [showMatchError, setShowMatchError] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("reason");
      setReason(null);
      setOther("");
      setConfirmInput("");
      setShowMatchError(false);
    }
  }, [open]);

  const expected = `Delete ${email}`;
  const canContinue = useMemo(() => {
    if (!reason) return false;
    if (reason === "Other") return other.trim().length > 0;
    return true;
  }, [reason, other]);
  const matches = confirmInput === expected;

  const handleDelete = async () => {
    if (!matches || !reason) {
      setShowMatchError(true);
      return;
    }
    try {
      await deleteAccount.mutateAsync({
        reason,
        other: reason === "Other" ? other.trim() : undefined,
      });
      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (err) {
      showActionToast({ title: t("account.deleteFailed"), description: t("account.deleteFailedDesc"), severity: "error" });
    }
  };

  const tertiaryButtonSx: SxProps<Theme> = {
    ...BUTTON_BASE,
    border: "none",
    bgcolor: "transparent",
    color: c.primary,
    "&:hover": { bgcolor: c.primarySecondaryHover },
    "&.Mui-disabled, &:disabled": { opacity: 0.5, cursor: "not-allowed" },
  };

  // Don't render until we know whether deletion is blocked, to avoid flashing
  // the reason step before a block modal takes over.
  if (!canDeleteQuery.isSuccess) return null;

  if (isBlocked) {
    return (
      <CenterModal
        open={open}
        onClose={onClose}
        title={t("auth.deleteAccount.cannotDelete.title")}
        hideCloseButton
        disableBackdropClick
        width={480}
      >
        <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: c.gray[700], mb: 1.5 }}>
          {t("auth.deleteAccount.cannotDelete.desc")}
        </Typography>
        <Box
          component="ul"
          sx={{ m: 0, mb: 2, pl: 3, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {blockingTeams.map((team) => (
            <Box
              component="li"
              key={team.id}
              sx={{ fontSize: 14, fontWeight: 500, color: c.gray[800] }}
            >
              {team.name}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              ...BUTTON_BASE,
              flex: "initial",
              minWidth: 120,
              px: 3,
              border: "none",
              bgcolor: c.primary,
              color: "white",
              "&:hover": { bgcolor: c.primaryHover },
            }}
          >
            {t("auth.deleteAccount.cannotDelete.ok")}
          </Box>
        </Box>
      </CenterModal>
    );
  }

  if (step === "reason") {
    return (
      <CenterModal
        open={open}
        onClose={onClose}
        title={t("auth.deleteAccount.reason.title")}
        hideCloseButton
        disableBackdropClick
        width={480}
      >
        <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: c.gray[700], mb: 2 }}>
          {t("auth.deleteAccount.reason.desc")}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {REASONS.map((r) => {
            const selected = reason === r;
            return (
              <Box
                key={r}
                onClick={() => setReason(r)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minHeight: 44,
                  px: 1.5,
                  py: 1,
                  borderRadius: "6px",
                  border: `1px solid ${selected ? c.primary : c.gray[300]}`,
                  bgcolor: selected ? c.primarySecondaryHover : "white",
                  cursor: "pointer",
                  "&:hover": { borderColor: selected ? c.primary : c.gray[400] },
                }}
              >
                <Radio
                  checked={selected}
                  size="small"
                  sx={{ p: 0, color: c.gray[400], "&.Mui-checked": { color: c.primary } }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: c.gray[800] }}>
                  {t(`auth.deleteAccount.reasons.${r}`)}
                </Typography>
              </Box>
            );
          })}

          {reason === "Other" && (
            <TextField
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder={t("auth.deleteAccount.reason.specify")}
              multiline
              minRows={3}
              fullWidth
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Box component="button" onClick={onClose} sx={tertiaryButtonSx}>
            {t("auth.deleteAccount.confirm.cancel")}
          </Box>
          <Box
            component="button"
            disabled={!canContinue}
            onClick={() => setStep("confirm")}
            sx={{
              ...BUTTON_BASE,
              border: "none",
              bgcolor: canContinue ? c.primary : c.gray[300],
              color: "white",
              cursor: canContinue ? "pointer" : "not-allowed",
              "&:hover": canContinue ? { bgcolor: c.primaryHover } : undefined,
            }}
          >
            {t("auth.deleteAccount.reason.continue")}
          </Box>
        </Box>
      </CenterModal>
    );
  }

  const showError = showMatchError && !matches;
  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("auth.deleteAccount.confirm.title")}
      hideCloseButton
      disableBackdropClick
      width={480}
    >
      <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: c.gray[700], mb: 2 }}>
        {t("auth.deleteAccount.confirm.desc", { email })}
      </Typography>

      <TextField
        value={confirmInput}
        onChange={(e) => {
          setConfirmInput(e.target.value);
          if (showMatchError) setShowMatchError(false);
        }}
        placeholder={t("auth.deleteAccount.confirm.placeholder", { email })}
        fullWidth
        error={showError}
        helperText={showError ? t("auth.deleteAccount.confirm.matchError", { email }) : undefined}
      />

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Box
          component="button"
          onClick={onClose}
          disabled={deleteAccount.isPending}
          sx={tertiaryButtonSx}
        >
          {t("auth.deleteAccount.confirm.cancel")}
        </Box>
        <Box
          component="button"
          onClick={handleDelete}
          disabled={deleteAccount.isPending}
          sx={{
            ...BUTTON_BASE,
            border: `1px solid ${c.destructive}`,
            bgcolor: "white",
            color: c.destructive,
            cursor: deleteAccount.isPending ? "wait" : "pointer",
            "&:hover": {
              borderColor: c.destructiveHover,
              bgcolor: c.destructiveSecondaryHover,
            },
            "&:active": {
              borderColor: c.destructivePressed,
              bgcolor: c.destructiveSecondaryPressed,
            },
          }}
        >
          {t("auth.deleteAccount.confirm.button")}
        </Box>
      </Box>
    </CenterModal>
  );
}
